import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  LinearProgress,
  Snackbar,
  Divider,
  useTheme,
  keyframes,
  styled,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Paper
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { Upload, CloudUpload, CheckCircle, Description } from '@mui/icons-material';
import { useFileUpload, useAnalysis } from '../hooks/useApi';
import { UPLOAD_CONFIG } from '../config/environment';
import GDPRConsentModal, { ConsentData, FileInfo } from '../components/GDPRConsentModal';
import AnalysisWaitingForm from '../components/AnalysisWaitingForm';
import { useConsent } from '../hooks/useConsent';
import { CustomerFormData } from '../components/CustomerForm';

// Gradient animation for title
const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 4s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
}));

const UploadPage: React.FC = () => {
  const theme = useTheme();
  const [files, setFiles] = useState<File[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showGDPRModal, setShowGDPRModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [mergeWarnings, setMergeWarnings] = useState<string[] | null>(null);
  const [powerUnit, setPowerUnit] = useState<'watt' | 'kilowatt'>('watt'); // Meeting 2025-10-03
  const [showWaitingForm, setShowWaitingForm] = useState(false);
  const [submittingLead, setSubmittingLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const navigate = useNavigate();

  // GDPR Consent Management
  const {
    hasValidConsent,
    saveConsent,
    hasPermission,
    isLoading: consentLoading,
    error: consentError
  } = useConsent();

  // Use custom hooks for API operations
  const {
    upload,
    uploading,
    progress,
    uploadResult,
    error: uploadError,
    reset: resetUpload
  } = useFileUpload({
    onSuccess: () => {
      setShowSuccess(true);
      // Analysis will be started by useEffect when uploadResult is available
    },
    onError: (error: any) => {
      console.error('Upload failed:', error);
    }
  });

  const {
    analysisResult,
    analyzing,
    error: analysisError,
    startAnalysis
  } = useAnalysis(uploadResult?.upload_id);

  // Start analysis when upload is successful and uploadResult is available
  React.useEffect(() => {
    if (uploadResult && uploadResult.upload_id && !analyzing && !analysisResult) {
      console.log('Starting analysis for upload:', uploadResult.upload_id);
      startAnalysis();
      // Show waiting form immediately when analysis starts (only if we have upload_id!)
      if (uploadResult.upload_id) {
        console.log('Opening form with upload_id:', uploadResult.upload_id);
        setShowWaitingForm(true);
      }
    }
  }, [uploadResult, startAnalysis, analyzing, analysisResult]);

  // Validate file before setting
  const validateFile = (file: File): string | null => {
    // Check file type
    if (!UPLOAD_CONFIG.allowedTypes.includes(file.type) && !file.name.endsWith('.csv')) {
      return 'Bitte wählen Sie eine CSV-Datei aus.';
    }

    // Check file size
    if (file.size > UPLOAD_CONFIG.maxFileSize) {
      const maxSizeMB = Math.round(UPLOAD_CONFIG.maxFileSize / (1024 * 1024));
      return `Die Datei ist zu groß. Maximale Größe: ${maxSizeMB}MB.`;
    }

    // Check if file is empty
    if (file.size === 0) {
      return 'Die Datei ist leer.';
    }

    return null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];

    if (selectedFiles.length === 0) {
      return;
    }

    // Validate all files
    let hasErrors = false;
    const errors: string[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const error = validateFile(file);
      if (error) {
        hasErrors = true;
        errors.push(`${file.name}: ${error}`);
      }
    }

    if (hasErrors) {
      setValidationError(errors.join('; '));
      setFiles([]);
      setPendingFiles([]);
      return;
    }

    // Check file count limit (max 10)
    if (selectedFiles.length > 10) {
      setValidationError('Maximal 10 Dateien erlaubt');
      setFiles([]);
      setPendingFiles([]);
      return;
    }

    // Clear previous validation errors
    setValidationError(null);
    setMergeWarnings(null);
    resetUpload(); // Reset previous upload state

    // Check if we have valid consent
    if (hasValidConsent) {
      // Consent is valid, proceed directly
      setFiles(selectedFiles);
      setPendingFiles([]);
    } else {
      // No valid consent, show GDPR modal
      setPendingFiles(selectedFiles);
      setShowGDPRModal(true);
    }
  };

  // GDPR Modal Handlers
  const handleGDPRAccept = async (consentData: ConsentData) => {
    try {
      const success = await saveConsent(consentData);
      if (success && pendingFiles.length > 0) {
        // Consent saved successfully, proceed with files
        setFiles(pendingFiles);
        setPendingFiles([]);
        setShowGDPRModal(false);
      } else {
        // Consent save failed
        setValidationError('Fehler beim Speichern der Einwilligung. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Consent save error:', error);
      setValidationError('Fehler beim Speichern der Einwilligung. Bitte versuchen Sie es erneut.');
    }
  };

  const handleGDPRDecline = () => {
    // User declined consent, reset file selection
    setPendingFiles([]);
    setFiles([]);
    setShowGDPRModal(false);
    // Reset file input
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleGDPRClose = () => {
    // Modal closed without decision, reset
    handleGDPRDecline();
  };

  const handleUpload = async () => {
    if (!files || files.length === 0) return;

    // Double-check consent before upload
    if (!hasValidConsent) {
      setValidationError('Keine gültige Einwilligung vorhanden. Bitte laden Sie die Datei(en) erneut hoch.');
      setFiles([]);
      return;
    }

    try {
      // Pass single file or array of files with power unit (Meeting 2025-10-03)
      const filesToUpload: File | File[] = files.length === 1 ? files[0] : files;
      const result = await upload(filesToUpload, powerUnit);

      // Check for merge warnings in multi-file uploads
      if ((result as any).merge_warnings) {
        setMergeWarnings((result as any).merge_warnings);
      }
    } catch (error) {
      console.error('Upload error:', error);
      // Error handling is managed by the useFileUpload hook
    }
  };

  // Navigate to battery loading page when form is submitted or skipped
  // The loading page will poll for progress and navigate to results when done
  React.useEffect(() => {
    if (uploadResult?.upload_id && leadSubmitted) {
      // Navigate to loading page after form submission or skip
      navigate(`/loading/${uploadResult.upload_id}`);
    }
  }, [uploadResult, navigate, leadSubmitted]);

  // Handle lead form submission
  const handleLeadSubmit = async (formData: CustomerFormData) => {
    setSubmittingLead(true);
    try {
      console.log('Submitting lead from UploadPage:', formData);
      const { leadService } = await import('../services/leadService');
      const response = await leadService.submitLead(formData);
      console.log('Lead submission successful:', response);

      setLeadSubmitted(true);
      setShowWaitingForm(false);

      // Navigation to loading page happens in useEffect
      // No alert - user will see progress immediately on loading page
    } catch (error: any) {
      console.error('Lead submission failed:', error);
      const errorMessage = error?.message || 'Unbekannter Fehler';
      alert(`Fehler beim Senden der Kontaktdaten: ${errorMessage}\n\nBitte versuchen Sie es später erneut.`);
    } finally {
      setSubmittingLead(false);
    }
  };

  // Handle skip - user wants to see results without filling form
  const handleSkipForm = () => {
    setShowWaitingForm(false);
    setLeadSubmitted(true); // Mark as "done" to allow navigation
    // Navigation to loading page happens in useEffect
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: 8
      }}
    >
      {/* Animated Background Layers */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}12 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}10 0%, transparent 50%),
            radial-gradient(circle at 60% 40%, ${theme.palette.info.main}08 0%, transparent 50%)
          `,
          animation: `${gradient} 8s ease-in-out infinite`,
          zIndex: 0,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '1200px', mx: 'auto', px: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <AnimatedTitle variant="h2" gutterBottom>
            CSV-Lastprofil hochladen
          </AnimatedTitle>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: { xs: '1rem', md: '1.2rem' },
              fontWeight: 300,
              opacity: 0.9
            }}
          >
            Laden Sie Ihr CSV-Leistungsprofil hoch, um eine Bewertung der Hybrid-Speicher-Eignung zu erhalten.
          </Typography>
        </Box>

      {/* CSV Template Link */}
      <Alert severity="info" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="body1" fontWeight={600} gutterBottom>
              Keine eigenen Messdaten?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Laden Sie eine passende CSV-Vorlage herunter und füllen Sie diese mit Ihren Werten.
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/vorlagen"
            variant="contained"
            startIcon={<Description />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Zu den Vorlagen
          </Button>
        </Box>
      </Alert>

      <Card
        elevation={0}
        sx={{
          mt: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.secondary.main}08 100%)`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${theme.palette.secondary.main}30`,
          boxShadow: `0 8px 32px ${theme.palette.secondary.main}20`,
        }}
      >
        <CardContent>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Datei auswählen
          </Typography>

          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              mb: 3,
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />

            <input
              accept=".csv"
              multiple
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload />}
                sx={{ mb: 2 }}
              >
                CSV-Datei(en) auswählen
              </Button>
            </label>

            <Typography variant="body2" color="text.secondary">
              Mehrere Dateien möglich - werden chronologisch zusammengeführt
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Unterstützte Formate: CSV-Dateien mit Leistungsdaten (max. 10 Dateien)
            </Typography>
          </Box>

          {/* Files selected success */}
          {files.length > 0 && !validationError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>{files.length} Datei(en) ausgewählt:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {files.map((file, idx) => (
                  <li key={idx}>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </li>
                ))}
              </ul>
              {files.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  Die Dateien werden chronologisch zusammengeführt und auf Duplikate geprüft.
                </Typography>
              )}
            </Alert>
          )}

          {/* Power Unit Selection (Meeting 2025-10-03) */}
          {files.length > 0 && !validationError && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 2,
                borderRadius: 2,
                border: `2px solid ${theme.palette.warning.light}`,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)'
              }}
            >
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2, color: theme.palette.warning.main }}>
                  ⚠️ WICHTIG: Leistungseinheit angeben
                </FormLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Bitte geben Sie an, in welcher Einheit die Leistungswerte in Ihrer CSV-Datei vorliegen:
                </Typography>
                <RadioGroup
                  row
                  value={powerUnit}
                  onChange={(e) => setPowerUnit(e.target.value as 'watt' | 'kilowatt')}
                >
                  <FormControlLabel
                    value="watt"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={600}>Watt (W)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Leistung in Watt
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="kilowatt"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={600}>Kilowatt (kW)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Leistung in Kilowatt
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Hinweis:</strong> Die korrekte Einheit ist wichtig für eine präzise Analyse.
                    Bei falscher Angabe können die Ergebnisse verfälscht werden.
                  </Typography>
                </Alert>
              </FormControl>
            </Paper>
          )}

          {/* Merge warnings */}
          {mergeWarnings && mergeWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Hinweise zur Zusammenführung:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {mergeWarnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Upload success */}
          {uploadResult && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
              Upload erfolgreich! Upload-ID: {uploadResult.upload_id}
            </Alert>
          )}

          {/* Validation errors */}
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          {/* Upload errors */}
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError.message}
            </Alert>
          )}

          {/* Analysis errors */}
          {analysisError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Analyse-Fehler: {analysisError.message}
            </Alert>
          )}

          {/* Consent errors */}
          {consentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              DSGVO-Fehler: {consentError}
            </Alert>
          )}

          {/* Upload progress */}
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Datei wird hochgeladen... {progress}%
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          {/* Analysis progress */}
          {analyzing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Analyse läuft... Dies kann einige Sekunden dauern.
              </Typography>
              <LinearProgress />
            </Box>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleUpload}
            disabled={files.length === 0 || uploading || analyzing || !!validationError || !hasValidConsent || consentLoading}
            startIcon={<Upload />}
          >
            {consentLoading ? 'Einwilligung wird gespeichert...' :
             uploading ? `Upload läuft... ${progress}%` :
             analyzing ? 'Analyse läuft...' :
             !hasValidConsent ? 'Datenschutz-Einwilligung erforderlich' :
             files.length > 1 ? `Analyse starten (${files.length} Dateien)` :
             'Analyse starten'}
          </Button>
        </CardContent>
      </Card>

      <Card
        elevation={0}
        sx={{
          mt: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.info.main}08 100%)`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${theme.palette.info.main}30`,
          boxShadow: `0 8px 32px ${theme.palette.info.main}20`,
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Anforderungen an die CSV-Datei
          </Typography>
          <Typography component="div">
            <ul>
              <li>CSV-Format mit Spaltenüberschriften</li>
              <li>Erforderliche Spalten: timestamp, current_A, voltage_V, power_W</li>
              <li>Mindestens 24 Stunden Messdaten</li>
              <li>Maximale Dateigröße: {Math.round(UPLOAD_CONFIG.maxFileSize / (1024 * 1024))} MB</li>
            </ul>
          </Typography>
        </CardContent>
      </Card>

      {/* Success notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Upload erfolgreich! Analyse wird gestartet...
        </Alert>
      </Snackbar>

      {/* GDPR Consent Modal */}
      <GDPRConsentModal
        open={showGDPRModal}
        onClose={handleGDPRClose}
        onAccept={handleGDPRAccept}
        onDecline={handleGDPRDecline}
        fileInfo={pendingFiles.length > 0 ? {
          name: pendingFiles.length === 1 ? pendingFiles[0].name : `${pendingFiles.length} Dateien`,
          size: pendingFiles.reduce((sum, f) => sum + f.size, 0),
          type: pendingFiles[0]?.type || 'text/csv'
        } : undefined}
      />

      {/* Analysis Waiting Form - shows during analysis */}
      {uploadResult?.upload_id && (
        <AnalysisWaitingForm
          open={showWaitingForm}
          uploadId={uploadResult.upload_id}
          analyzing={analyzing}
          onSubmit={handleLeadSubmit}
          onSkip={handleSkipForm}
          submitting={submittingLead}
        />
      )}
      </Box>
    </Box>
  );
};

export default UploadPage;

